import React, { useEffect, useRef, useState } from 'react';
import { AuthProvider, useAuth } from './auth/AuthContext.jsx';
import Login from './auth/Login.jsx';

// Styles
import './styles/index.css';
import { getRoomAlerts, getRoomDisplayMeta } from './utils';

// Hooks
import { useTheme, useSensorConfig, useMQTT, useNotificationConfig } from './hooks';

// Components
import {
  BackgroundWaves,
  Loading,
  Sidebar,
  SensorConfigPanel,
  NotificationConfigPanel,
  HistoryConfigPanel,
  RoomSelector,
  DeviceCard,
  TemperatureSection,
  OxygenSection,
  ChartsSection,
  GaugesSection,
  StatsSection,
  HistoricalView,
  IncidentPanel,
  IncidentsLogPanel,
  RoomsHeader,
  DashboardHeader,
  Footer,
  TexcumarPage,
} from './components';

const ALERT_LEVEL_WEIGHT = {
  critical: 2,
  warning: 1,
};

const pickRepresentativeAlert = (alerts) => {
  if (!alerts.length) return null;

  return alerts.reduce((selected, current) => {
    if (!selected) return current;

    const selectedWeight = ALERT_LEVEL_WEIGHT[selected.level] || 0;
    const currentWeight = ALERT_LEVEL_WEIGHT[current.level] || 0;

    if (currentWeight !== selectedWeight) {
      return currentWeight > selectedWeight ? current : selected;
    }

    if ((current.deviation || 0) !== (selected.deviation || 0)) {
      return (current.deviation || 0) > (selected.deviation || 0) ? current : selected;
    }

    return current;
  }, null);
};

const isTexcumarPath = () => window.location.pathname === '/texcumar' || window.location.pathname.startsWith('/texcumar/');

/**
 * Main App Component
 */
function InnerApp() {
  // View state: 'rooms', 'dashboard' or 'texcumar'
  const [currentView, setCurrentView] = useState(() => isTexcumarPath() ? 'texcumar' : 'rooms');
  const [selectedRoom, setSelectedRoom] = useState(null);

  // Dashboard tab state: 'realtime' or 'historical'
  const [dashboardTab, setDashboardTab] = useState('realtime');

  // Sidebar and config panel state
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const [isNotificationConfigOpen, setIsNotificationConfigOpen] = useState(false);
  const [isHistoryConfigOpen, setIsHistoryConfigOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [trackedAlert, setTrackedAlert] = useState(null);
  const [isIncidentsLogOpen, setIsIncidentsLogOpen] = useState(false);
  const previousAlertsRef = useRef({});
  const lastNotificationSentRef = useRef({});

  // Custom hooks
  const { isDarkTheme, toggleTheme, themeClass } = useTheme();
  const { sensorConfig, handleSaveConfig, refreshInterval } = useSensorConfig();
  const { notificationConfig, handleSaveNotificationConfig } = useNotificationConfig();
  const { rooms, currentTime, getRoomData } = useMQTT(refreshInterval);

  useEffect(() => {
    const handlePathChange = () => setCurrentView(isTexcumarPath() ? 'texcumar' : 'rooms');
    window.addEventListener('popstate', handlePathChange);
    return () => window.removeEventListener('popstate', handlePathChange);
  }, []);

  useEffect(() => {
    const nextAlertMap = {};
    const generatedNotifications = [];

    Object.values(rooms).forEach((room) => {
      if (!room?.code || !room?.hasData || !room?.data) return;

      const roomAlerts = getRoomAlerts(
        room.code,
        room.data,
        sensorConfig,
        room.lastUpdate || Date.now()
      );
      const alertsByCategory = roomAlerts.reduce((acc, alert) => {
        if (!acc[alert.category]) acc[alert.category] = [];
        acc[alert.category].push(alert);
        return acc;
      }, {});
      const activeAlerts = Object.values(alertsByCategory)
        .map((alerts) => pickRepresentativeAlert(alerts))
        .filter(Boolean);

      nextAlertMap[room.code] = activeAlerts.reduce((acc, alert) => {
        acc[alert.category] = alert.statusKey;
        return acc;
      }, {});

      activeAlerts.forEach((alert) => {
        if (!notificationConfig?.notificationLevels?.[alert.level]) {
          return;
        }

        const cooldownSeconds = Number(notificationConfig?.notificationTimings?.[alert.category] || 0);
        const cooldownMs = Math.max(cooldownSeconds, 0) * 1000;
        const deliveryKey = `${room.code}:${alert.category}`;
        const lastSentAt = lastNotificationSentRef.current[deliveryKey] || 0;
        const canSendByCooldown = !lastSentAt || !cooldownMs || (alert.createdAt - lastSentAt) >= cooldownMs;

        if (canSendByCooldown) {
          lastNotificationSentRef.current[deliveryKey] = alert.createdAt;
          generatedNotifications.push(alert);
        }
      });
    });

    previousAlertsRef.current = nextAlertMap;

    if (generatedNotifications.length > 0) {
      setNotifications((prev) => [...generatedNotifications.reverse(), ...prev].slice(0, 30));
    }
  }, [rooms, sensorConfig, notificationConfig]);

  // Handle room selection
  const handleSelectRoom = (roomCode) => {
    setSelectedRoom(roomCode);
    setCurrentView('dashboard');
  };

  // Handle back to rooms
  const handleBackToRooms = () => {
    window.history.pushState({}, '', '/');
    setCurrentView('rooms');
    setSelectedRoom(null);
    setDashboardTab('realtime');
  };

  const handleOpenTexcumar = () => {
    window.history.pushState({}, '', '/texcumar');
    setCurrentView('texcumar');
    setIsSidebarOpen(false);
  };

  const handleOpenNotifications = () => {
    setNotifications((prev) =>
      prev.map((notification) => ({ ...notification, isRead: true }))
    );
  };

  const handleClearNotifications = () => {
    const now = Date.now();
    notifications.forEach((n) => {
      lastNotificationSentRef.current[`${n.roomCode}:${n.category}`] = now;
    });
    setNotifications([]);
  };

  const handleTrackAlert = (notification) => {
    setTrackedAlert(notification);
  };

  const handleNotificationClick = (notification) => {
    window.history.pushState({}, '', '/');
    setSelectedRoom(notification.roomCode);
    setCurrentView('dashboard');
    setDashboardTab('realtime');
  };

  // Get current room data
  const roomData = getRoomData(selectedRoom);
  const {
    roomData: currentRoomData,
    latestData,
    hasReceivedData,
    history,
    previousData,
    messageCount,
  } = roomData || {};

  // Render Room Selector View
  const { user, logout } = useAuth();
  const perms = (user && user.permisos) || {};
  const isAdmin = !!(user && user.es_admin);
  const canConfigThresholds = isAdmin || !!perms['config:thresholds'];
  const canConfigHistory = isAdmin || !!perms['config:history'];
  const unreadNotifications = notifications.filter((notification) => !notification.isRead).length;

  const openSidebar = () => setIsSidebarOpen(true); // Siempre abre el sidebar
  const openConfig = () => {
    if (canConfigThresholds) setIsConfigOpen(true);
  };
  const openNotificationConfig = () => {
    if (canConfigThresholds) setIsNotificationConfigOpen(true);
  };
  const openHistoryConfig = () => {
    if (canConfigHistory) setIsHistoryConfigOpen(true);
  };

  if (!user) {
    return <Login />;
  }

  if (currentView === 'texcumar') {
    return (
      <div className={`app-container ${themeClass}`}>
        <BackgroundWaves />
        <RoomsHeader
          isDarkTheme={isDarkTheme}
          onToggleTheme={toggleTheme}
          onOpenSidebar={openSidebar}
          userName={user?.nombre || user?.email}
          notifications={notifications}
          unreadCount={unreadNotifications}
          onOpenNotifications={handleOpenNotifications}
          onNotificationClick={handleNotificationClick}
          onClearNotifications={handleClearNotifications}
          onTrackAlert={handleTrackAlert}
        />
        <TexcumarPage />
        <Sidebar
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
          onOpenTexcumar={handleOpenTexcumar}
          onOpenConfig={openConfig}
          onOpenNotificationConfig={openNotificationConfig}
          onOpenHistoryConfig={openHistoryConfig}
          onOpenIncidentsLog={() => setIsIncidentsLogOpen(true)}
          canConfigThresholds={canConfigThresholds}
          canConfigHistory={canConfigHistory}
          onLogout={logout}
        />
        <SensorConfigPanel isOpen={isConfigOpen} onClose={() => setIsConfigOpen(false)} config={sensorConfig} onSave={handleSaveConfig} />
        <NotificationConfigPanel isOpen={isNotificationConfigOpen} onClose={() => setIsNotificationConfigOpen(false)} config={notificationConfig} onSave={handleSaveNotificationConfig} />
        {canConfigHistory && <HistoryConfigPanel isOpen={isHistoryConfigOpen} onClose={() => setIsHistoryConfigOpen(false)} />}
        <IncidentPanel alert={trackedAlert} onClose={() => setTrackedAlert(null)} />
        <IncidentsLogPanel isOpen={isIncidentsLogOpen} onClose={() => setIsIncidentsLogOpen(false)} />
        <Footer />
      </div>
    );
  }

  if (currentView === 'rooms') {
    return (
      <div className={`app-container ${themeClass}`}>
        <BackgroundWaves />

        <RoomsHeader
          isDarkTheme={isDarkTheme}
          onToggleTheme={toggleTheme}
          onOpenSidebar={openSidebar}
          userName={user?.nombre || user?.email}
          notifications={notifications}
          unreadCount={unreadNotifications}
          onOpenNotifications={handleOpenNotifications}
          onNotificationClick={handleNotificationClick}
          onClearNotifications={handleClearNotifications}
          onTrackAlert={handleTrackAlert}
        />

        <RoomSelector rooms={rooms} onSelectRoom={handleSelectRoom} sensorConfig={sensorConfig} />

        <IncidentPanel alert={trackedAlert} onClose={() => setTrackedAlert(null)} />
        <IncidentsLogPanel isOpen={isIncidentsLogOpen} onClose={() => setIsIncidentsLogOpen(false)} />

        <Sidebar
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
          onOpenTexcumar={handleOpenTexcumar}
          onOpenConfig={openConfig}
          onOpenNotificationConfig={openNotificationConfig}
          onOpenHistoryConfig={openHistoryConfig}
          onOpenIncidentsLog={() => setIsIncidentsLogOpen(true)}
          canConfigThresholds={canConfigThresholds}
          canConfigHistory={canConfigHistory}
          onLogout={logout}
        />

        <SensorConfigPanel
          isOpen={isConfigOpen}
          onClose={() => setIsConfigOpen(false)}
          config={sensorConfig}
          onSave={handleSaveConfig}
        />

        <NotificationConfigPanel
          isOpen={isNotificationConfigOpen}
          onClose={() => setIsNotificationConfigOpen(false)}
          config={notificationConfig}
          onSave={handleSaveNotificationConfig}
        />

        {canConfigHistory && (
          <HistoryConfigPanel
          isOpen={isHistoryConfigOpen}
          onClose={() => setIsHistoryConfigOpen(false)}
        />)}

        <Footer />
      </div>
    );
  }

  // Render Dashboard View
  return (
    <div className={`app-container ${themeClass}`}>
      <BackgroundWaves />

      <DashboardHeader
        selectedRoom={getRoomDisplayMeta(selectedRoom).displayName}
        hasReceivedData={hasReceivedData}
        isDarkTheme={isDarkTheme}
        onToggleTheme={toggleTheme}
        onOpenSidebar={openSidebar}
        userName={user?.nombre || user?.email}
        onBackToRooms={handleBackToRooms}
        notifications={notifications}
        unreadCount={unreadNotifications}
        onOpenNotifications={handleOpenNotifications}
        onNotificationClick={handleNotificationClick}
        onClearNotifications={handleClearNotifications}
        onTrackAlert={handleTrackAlert}
      />

      <IncidentPanel alert={trackedAlert} onClose={() => setTrackedAlert(null)} />
      <IncidentsLogPanel isOpen={isIncidentsLogOpen} onClose={() => setIsIncidentsLogOpen(false)} />

      <Sidebar
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        onOpenTexcumar={handleOpenTexcumar}
        onOpenConfig={openConfig}
        onOpenNotificationConfig={openNotificationConfig}
        onOpenHistoryConfig={openHistoryConfig}
        onOpenIncidentsLog={() => setIsIncidentsLogOpen(true)}
        canConfigThresholds={canConfigThresholds}
        canConfigHistory={canConfigHistory}
        onLogout={logout}
      />

      <SensorConfigPanel
        isOpen={isConfigOpen}
        onClose={() => setIsConfigOpen(false)}
        config={sensorConfig}
        onSave={handleSaveConfig}
      />

      <NotificationConfigPanel
        isOpen={isNotificationConfigOpen}
        onClose={() => setIsNotificationConfigOpen(false)}
        config={notificationConfig}
        onSave={handleSaveNotificationConfig}
      />

      {canConfigHistory && (
        <HistoryConfigPanel
          isOpen={isHistoryConfigOpen}
          onClose={() => setIsHistoryConfigOpen(false)}
        />
      )}

      <main className="main-content">
        {/* Dashboard Tabs */}
        <div className="dashboard-tabs">
          <button
            className={`dashboard-tab ${dashboardTab === 'realtime' ? 'active' : ''}`}
            onClick={() => setDashboardTab('realtime')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
            </svg>
            <span>Tiempo Real</span>
          </button>
          <button
            className={`dashboard-tab ${dashboardTab === 'historical' ? 'active' : ''}`}
            onClick={() => setDashboardTab('historical')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
            <span>Histórico</span>
          </button>
        </div>

        {dashboardTab === 'realtime' ? (
          // Real-time dashboard
          !hasReceivedData || !latestData ? (
            <Loading text="Esperando datos del sensor..." />
          ) : (
            <>
              <DeviceCard
                latestData={latestData}
                lastUpdate={currentRoomData?.lastUpdate}
                currentTime={currentTime}
                messageCount={messageCount}
                historyLength={history.length}
                refreshInterval={refreshInterval}
              />

              <TemperatureSection
                latestData={latestData}
                previousData={previousData}
                sensorConfig={sensorConfig}
              />

              <OxygenSection
                latestData={latestData}
                previousData={previousData}
                sensorConfig={sensorConfig}
              />

              <ChartsSection
                history={history}
                latestData={latestData}
                sensorConfig={sensorConfig}
              />

              <GaugesSection latestData={latestData} />

              <StatsSection history={history} />
            </>
          )
        ) : (
          // Historical view
          <HistoricalView
            roomCode={selectedRoom}
            sensorConfig={sensorConfig}
          />
        )}
      </main>

      <Footer />
    </div>
  );
}
export default function App() {
  return (
    <AuthProvider>
      <InnerApp />
    </AuthProvider>
  );
}
